/* eslint-disable import/no-default-export */

import { useNavigation, useRoute } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import jwt_decode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { useTranslation } from 'react-i18next';

import { Alert } from '@components/Alert/index.web';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { useNotification } from '@hooks/useNotification.web';
import { useAuthContext } from '@src/context/authContext';
import { buildAppleVerifyMissContext } from '@utils/appleCredential';
import { getApiConfig } from '@utils/getApiConfig';
import { AsyncStorage, SecurityStorage } from '@utils/storage';

import { Box, Text } from '../Restyle';

type OnboardingModalProps = {
  onSelect: (type: string) => void;
};

type UserInfo = {
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  provider?: string;
  identity_token?: string;
};

const OnboardingModal: React.FC<OnboardingModalProps> = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState<boolean>(false);
  const { prompt, userInfo, clearUserInfo, setUserDetail } = useGoogleAuth();
  const apiConfig = getApiConfig();
  const { setAuthToken } = useAuthContext();
  const { params } = useRoute();
  const { t } = useTranslation('models');
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [errorName, setErrorName] = useState('');

  useEffect(() => {
    userInfo && setLoading(true);
    userInfo && verifyUserEmail(userInfo);
  }, [userInfo]);

  const { registerForPushNotificationsAsync } = useNotification();
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await registerForPushNotificationsAsync();
        setDeviceToken(token || null);
      } catch (error) {
        console.error('Failed to get device token', error);
        setDeviceToken(null);
      }
    };

    fetchToken();
  }, []);

  const verifyUserEmail = async (userInfo: UserInfo) => {
    const verifyUser = `/api/v3/verify_user.json`;
    const userPayload = {
      user: {
        email: userInfo?.email?.toLowerCase(),
        provider: userInfo?.provider,
        uid: userInfo?.id,
        is_verified: false,
        firebase_token: deviceToken,
        platform: 'web',
        // TA-10975: proves the caller holds a live Apple/Google credential
        // for this account — see AppleIdentityTokenVerifier /
        // GoogleIdentityTokenVerifier on the backend.
        identity_token: userInfo?.identity_token,
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${verifyUser}`;
    await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userPayload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        setLoading(false);
        if (response?.error) {
          if (response.auth_provider) {
            setShowProviderModal(true);
            setErrorName(response.error);
          } else {
            // TA-8178: this function also handles Google's "no existing
            // account" case, so scope the event to Apple — mirrors the
            // native OnboardingModal instrumentation; see
            // buildAppleVerifyMissContext for why the payload is reduced
            // instead of logged raw.
            if (userPayload.user.provider === 'Apple') {
              const verifyMissContext = buildAppleVerifyMissContext(
                userPayload.user.uid,
                userPayload.user.provider,
                userPayload.user.email,
                response.error
              );
              Sentry.captureMessage(
                'Apple verify_user: no existing account found',
                {
                  level: 'info',
                  // uid stays in extra only — it is unique per user, and a
                  // per-user tag value is exactly the high-cardinality tag
                  // Sentry advises against.
                  tags: {
                    appleVerifyProvider: verifyMissContext.provider,
                  },
                  extra: verifyMissContext,
                }
              );
            }
            navigation.navigate('register', {
              socialSignUp: true,
            });
          }
        } else if (response.user) {
          try {
            window.sessionStorage.setItem('invitation_token', '');
            await AsyncStorage.setItem('keepSignedIn', 'true');
            window.sessionStorage.setItem('setKeepSignedIn', '1');

            await SecurityStorage.setItem(
              'authToken',
              response.user.api_tokens[0].token
            );
          } catch (e) {
            console.log('An error happened, ', e?.toString());
          } finally {
            Sentry.setContext('userInfo', {
              email: response?.user?.email || userInfo?.email,
              username: response?.user?.name || userInfo?.given_name,
              id: response?.user?.id || userInfo?.id,
              provider: userInfo?.provider,
            });
            setAuthToken(response.user.api_tokens[0].token);
          }
        }
      })
      .catch((_err) => {});
  };

  const appleSignInWeb = async (response: any) => {
    try {
      const localResponse = await AsyncStorage.getItem('appleUserInfo');
      let decodeEmail = '';

      const token = response.authorization.id_token;
      const decoded = jwt_decode<{ sub: string; email?: string }>(token);
      if (decoded) {
        decodeEmail = decoded.email ?? '';
      }
      if (!decoded?.sub) {
        Alert.alert('TaskTag', t('onBoarding.error.appleSignInFailed'));
        return;
      }

      const userData = localResponse ? JSON.parse(localResponse) : null;
      const userInfoPayload = {
        id: decoded.sub,
        email: response?.user?.email || userData?.user?.email || decodeEmail,
        given_name:
          response?.user?.name?.firstName || userData?.user?.name?.firstName,
        family_name:
          response?.user?.name?.lastName || userData?.user?.name?.lastName,
        provider: 'Apple',
        // TA-10975: the raw token, not the decoded claims — only this is
        // what the backend can still verify server-side.
        identity_token: token,
      };
      if (response.user?.email) {
        await AsyncStorage.setItem('appleUserInfo', JSON.stringify(response));
        setUserDetail && setUserDetail(userInfoPayload);
      } else if (response) {
        setUserDetail && setUserDetail(userInfoPayload);
      } else {
        Alert.alert('TaskTag', t('onBoarding.error.emailNotFound'));
      }
    } catch (e) {
      console.log('An error happened, ', e?.toString());
    }
  };

  const buttonName = params?.isLogin
    ? t('onBoarding.modal.logInWithEmail')
    : t('onBoarding.modal.signUpWithEmail');

  const title = params?.isLogin ? 'Login' : 'Get started with TaskTag';
  return (
    <Box backgroundColor='grey03' height='100%'>
      <Box
        marginBottom='m'
        marginHorizontal='m'
        alignItems='center'
        justifyContent='center'
        height='100%'>
        <Box
          alignItems='center'
          borderRadius='m'
          borderColor='grey02'
          borderWidth={1}
          py='l'
          px='xxl'
          backgroundColor='white'
          style={{
            position: 'absolute',
            height: 472,
            maxHeight: 472,
            width: 533,
            overflow: 'hidden',
          }}>
          <Icon name='Logo' width={106} height={32} marginBottom='xl' />
          <Text marginBottom='l' variant='labelLarge'>
            {title}
          </Text>
          <Button
            onPress={() => {
              prompt();
            }}
            prefix={
              <Icon
                variant='l'
                marginLeft='s'
                name='Google'
                color='textPrimary'
                accessibilityLabel='googleIcon'
              />
            }
            marginTop='s'
            disabled={loading}
            borderWidth={1}
            borderColor='textPrimary'
            backgroundColor='white'
            accessibilityLabel={t('onBoarding.modal.continueWithGoogle')}
            alignTextLeft
            fullWidth>
            <Text variant='buttonLabel' color='textPrimary'>
              {t('onBoarding.modal.continueWithGoogle')}
            </Text>
          </Button>
          <AppleSignin
            /** Auth options passed to AppleID.auth.init()  */
            authOptions={{
              clientId: process.env.APPLE_CLIENT_ID_WEB,
              scope: 'name email',
              redirectURI: process.env.APPLE_REDIRECT_UI,
              state: '',
              nonce: 'nonce',
              usePopup: true,
            }}
            render={(props) => (
              <Button
                fullWidth
                borderWidth={1}
                borderColor='textPrimary'
                backgroundColor='white'
                alignTextLeft
                marginTop='s'
                accessibilityLabel={t('onBoarding.modal.continueWithApple')}
                prefix={
                  <Icon
                    variant='l'
                    marginLeft='s'
                    name='Apple'
                    color='textPrimary'
                    accessibilityLabel={t('onBoarding.modal.continueWithApple')}
                  />
                }
                {...props}>
                <Text variant='buttonLabel' color='textPrimary'>
                  {t('onBoarding.modal.continueWithApple')}
                </Text>
              </Button>
            )}
            uiType='light'
            className='apple-auth-btn'
            onSuccess={(response: any) => appleSignInWeb(response)} // default = undefined
            // eslint-disable-next-line no-console
            onError={(error: any) => console.error('error', error)} // default = undefined
          />

          <Button
            onPress={() => {
              navigation.pop();
              clearUserInfo && clearUserInfo();
              {
                params?.isLogin
                  ? navigation.navigate('login-modal')
                  : navigation.navigate('register', { routeName: 'email' });
              }
            }}
            prefix={
              <Icon
                variant='l'
                marginLeft='s'
                name='Mail'
                color='textPrimary'
              />
            }
            borderWidth={1}
            marginTop='s'
            borderColor='textPrimary'
            backgroundColor='white'
            alignTextLeft
            fullWidth>
            <Text variant='buttonLabel' color='textPrimary'>
              {buttonName}
            </Text>
          </Button>
        </Box>
      </Box>
      {showProviderModal && (
        <ConfirmModal
          showModal={showProviderModal}
          onClose={() => setShowProviderModal(false)}
          onPress={() => setShowProviderModal(false)}
          buttonText={t('onBoarding.error.okPress')}
          title={t('onBoarding.error.title')}
          message={t('onBoarding.error.userAlreadyRegisteredWithProvider', {
            provider: errorName,
          })}
        />
      )}
    </Box>
  );
};

export default OnboardingModal;
